import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Plus, Wrench } from "lucide-react";
import { useState } from "react";
import type { ToolAccess, ToolHealth, ToolRisk } from "@zworks/console-contracts";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  LoadingState,
  NotConnectedState,
} from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { ApiError, removeTool, requestTool, toolsQuery } from "@/lib/api";
import { PageHeader } from "@/components/bespoke";
import { CapabilityDrawer, type CapabilityDrawerItem } from "@/components/capability-drawer";
import { AddToolFromIntegrationDialog } from "./tools-from-integration";
import { formatExpiry } from "@/lib/format";
import { useCurrentProject } from "@/lib/project";

const healthBadges: Record<
  ToolHealth,
  { label: string; variant: "success" | "warning" | "danger" | "unknown" }
> = {
  healthy: { label: "Healthy", variant: "success" },
  degraded: { label: "Degraded", variant: "warning" },
  unreachable: { label: "Unreachable", variant: "danger" },
  unknown: { label: "Unknown", variant: "unknown" },
};

const riskBadges: Record<ToolRisk, { label: string; variant: "success" | "warning" | "danger" }> = {
  read: { label: "Low", variant: "success" },
  write: { label: "Medium", variant: "warning" },
  destructive: { label: "High", variant: "danger" },
};

const accessLabels: Record<ToolAccess, string> = {
  read: "Read",
  write: "Write",
  "read-write": "Read and write",
};

function Summary({
  attached,
  healthy,
  usedByFlows,
}: {
  attached: number;
  healthy: number;
  usedByFlows: number;
}) {
  const cards = [
    { label: "Attached", value: String(attached) },
    { label: "Healthy tools", value: String(healthy) },
    { label: "Used by workflows", value: String(usedByFlows) },
  ];
  return (
    <dl className="grid gap-3 sm:grid-cols-3">
      {cards.map((card) => (
        <div key={card.label} className="rounded-lg border bg-card px-4 py-3">
          <dt className="text-xs font-medium text-muted-foreground">{card.label}</dt>
          <dd className="mt-1 text-2xl font-semibold tabular-nums">{card.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ContractScore({ score }: { score: number | null | undefined }) {
  if (score === null || score === undefined) {
    return <Badge variant="unknown">Not run</Badge>;
  }
  return (
    <Badge variant={score >= 80 ? "success" : score >= 60 ? "warning" : "danger"}>{score}%</Badge>
  );
}

function AddToolMenu({ onFromIntegration }: { onFromIntegration: () => void }) {
  return (
    <div className="relative">
      <DropdownMenu>
        <DropdownMenuTrigger className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90">
          Add tool
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/* The menu returns focus to its trigger after the click, so the dialog opens afterwards. */}
          <DropdownMenuItem onClick={() => queueMicrotask(onFromIntegration)}>
            From an integration
          </DropdownMenuItem>
          <DropdownMenuItem disabled title="MCP server connections land with the integrations flow">
            Connect an MCP server
          </DropdownMenuItem>
          <DropdownMenuItem disabled title="OpenAPI upload lands with the tool upload flow">
            Upload an OpenAPI spec
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** The Studio tools tab: attached tools with source, access, risk, contract tests, use, and state. */
export function ToolsPage({ headingLevel = "h1" }: { headingLevel?: "h1" | "h2" }) {
  const { project, loaded } = useCurrentProject();
  const queryClient = useQueryClient();
  const tools = useQuery({ ...toolsQuery(project?.id ?? ""), enabled: loaded && project !== null });
  const [name, setName] = useState("");
  const [capabilities, setCapabilities] = useState("");
  const [credentialRef, setCredentialRef] = useState("");
  const [source, setSource] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [integrationOpen, setIntegrationOpen] = useState(false);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["projects", project?.id, "tools"] });
  };
  const requestMutation = useMutation({
    mutationFn: () =>
      requestTool(project!.id, {
        tool: name.trim(),
        capabilities: capabilities
          .split(",")
          .map((capability) => capability.trim())
          .filter((capability) => capability.length > 0),
        credentialRef: credentialRef.trim(),
        ...(source.trim() ? { source: source.trim() } : {}),
      }),
    onSuccess: async () => {
      setName("");
      setCapabilities("");
      setCredentialRef("");
      setSource("");
      setFeedback("Tool requested.");
      refresh();
    },
    onError: () => setFeedback("The tool could not be requested. Check the details and try again."),
  });

  if (!loaded || (project !== null && tools.isPending)) {
    return (
      <>
        <PageHeader title="Tools" headingLevel={headingLevel} />
        <LoadingState label="Loading tools" rows={5} />
      </>
    );
  }

  if (project === null) {
    return (
      <>
        <PageHeader title="Tools" headingLevel={headingLevel} />
        <EmptyState
          title="No projects yet"
          description="Create a project to attach the tools its runs may use."
        />
      </>
    );
  }

  if (tools.isError) {
    const error = tools.error;
    return (
      <>
        <PageHeader title="Tools" headingLevel={headingLevel} />
        {error instanceof ApiError && error.status === 404 ? (
          <ForbiddenState
            title="You do not have access to this project"
            description="Ask an owner to invite you. Nothing from the project is shown."
          />
        ) : (
          <ErrorState
            title="Tools could not be loaded"
            description={
              error instanceof ApiError
                ? (error.problem?.detail ?? error.message)
                : "The request could not be completed."
            }
            requestId={error instanceof ApiError ? (error.requestId ?? "unknown") : "unknown"}
            onRetry={() => void tools.refetch()}
          />
        )}
      </>
    );
  }

  const toolRows = tools.data ?? [];
  const healthy = toolRows.filter((row) => row.health === "healthy").length;
  const usedByFlows = new Set(toolRows.flatMap((row) => row.usedByFlows)).size;
  const selected = toolRows.find((row) => row.id === selectedId) ?? null;
  const selectedItem: CapabilityDrawerItem | null = selected
    ? {
        name: selected.tool,
        detail: selected.credentialRef,
        permissions: selected.capabilities,
        version: undefined,
        source: selected.source ?? selected.integrationType,
        state: healthBadges[selected.health],
        workflows: selected.usedByFlows,
      }
    : null;
  const canRequest =
    name.trim().length > 0 && capabilities.trim().length > 0 && credentialRef.trim().length > 0;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Tools"
        headingLevel={headingLevel}
        actions={<AddToolMenu onFromIntegration={() => setIntegrationOpen(true)} />}
      />

      <AddToolFromIntegrationDialog
        projectId={project!.id}
        open={integrationOpen}
        onOpenChange={setIntegrationOpen}
        onAdded={() => {
          setFeedback("Tool added.");
        }}
      />

      <Summary attached={toolRows.length} healthy={healthy} usedByFlows={usedByFlows} />

      {feedback && (
        <p role="status" className="text-sm text-muted-foreground">
          {feedback}
        </p>
      )}

      <section aria-labelledby="attached-tools-heading" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="attached-tools-heading" className="text-base font-semibold">
            Attached tools
          </h2>
          <span className="text-sm text-muted-foreground">{toolRows.length} attached</span>
        </div>
        {toolRows.length === 0 ? (
          <NotConnectedState
            title="No tools attached yet"
            description="Requested tools are recorded here with the capabilities you name. The run runtime does not read attachments yet, so no run can use them."
          />
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <caption className="sr-only">
                    Attached tools with source, access, risk, contract tests, use, and state
                  </caption>
                  <thead>
                    <tr>
                      {[
                        "Tool",
                        "Comes from",
                        "Access",
                        "Risk",
                        "Contract tests",
                        "Used in",
                        "State",
                        "Credential",
                      ].map((label) => (
                        <th
                          key={label}
                          scope="col"
                          className="h-10 border-b px-3 text-left text-xs font-medium text-muted-foreground first:pl-5 last:pr-5"
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {toolRows.map((row) => {
                      const health = healthBadges[row.health];
                      const risk = row.risk ? riskBadges[row.risk] : null;
                      return (
                        <tr key={row.id} className="hover:bg-muted/50">
                          <td className="border-b px-3 py-3 first:pl-5">
                            <button
                              type="button"
                              className="rounded-sm text-left font-mono text-[0.8125rem] underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                              onClick={() => setSelectedId(row.id)}
                              aria-label={`Open ${row.tool} details`}
                            >
                              {row.tool}
                            </button>
                            <ul className="mt-1 flex flex-col gap-1 text-xs text-muted-foreground">
                              {row.capabilities.map((capability) => (
                                <li key={capability}>{capability}</li>
                              ))}
                            </ul>
                          </td>
                          <td className="border-b px-3 py-3 text-muted-foreground">
                            {row.source ?? row.integrationType ?? "Not recorded"}
                          </td>
                          <td className="border-b px-3 py-3">
                            {row.access ? accessLabels[row.access] : "Read"}
                          </td>
                          <td className="border-b px-3 py-3">
                            {risk ? (
                              <Badge variant={risk.variant}>{risk.label}</Badge>
                            ) : (
                              <Badge variant="warning">Medium</Badge>
                            )}
                          </td>
                          <td className="border-b px-3 py-3">
                            <ContractScore score={row.contractTestScore} />
                          </td>
                          <td className="border-b px-3 py-3">
                            {row.usedByFlows.length === 0 ? (
                              <span className="text-muted-foreground">No workflows yet</span>
                            ) : (
                              <ul className="flex flex-col gap-1">
                                {row.usedByFlows.map((flow) => (
                                  <li key={flow}>{flow}</li>
                                ))}
                              </ul>
                            )}
                          </td>
                          <td className="border-b px-3 py-3">
                            <Badge variant={health.variant}>{health.label}</Badge>
                          </td>
                          <td className="border-b px-3 py-3 last:pr-5">
                            <span className="flex items-center gap-1.5 font-mono text-[0.8125rem]">
                              <KeyRound aria-hidden className="size-3.5 shrink-0" />
                              {row.credentialRef}
                            </span>
                            <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                              <Badge variant={row.credentialValid ? "success" : "danger"}>
                                {row.credentialValid ? "Valid" : "Invalid"}
                              </Badge>
                              <span className="text-xs tabular-nums text-muted-foreground">
                                Expires {formatExpiry(row.credentialExpiresAt)}
                              </span>
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="flex items-center gap-2 border-t px-5 py-3 text-xs text-muted-foreground">
                <KeyRound aria-hidden className="size-3.5 shrink-0" />
                Credentials are references and are never shown. A tool grants no more authority than
                its bound credential.
              </p>
            </CardContent>
          </Card>
        )}
      </section>

      <section aria-labelledby="request-tool-heading">
        <Card>
          <CardContent>
            <h2 id="request-tool-heading" className="flex items-center gap-2 text-sm font-medium">
              <Wrench aria-hidden className="size-4" />
              Request a tool
            </h2>
            <form
              className="mt-3 grid gap-3 sm:grid-cols-2"
              onSubmit={(event) => {
                event.preventDefault();
                requestMutation.mutate();
              }}
            >
              <label className="grid gap-1 text-sm">
                Tool name
                <Input
                  aria-label="Tool name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="jira"
                />
              </label>
              <label className="grid gap-1 text-sm">
                Credential reference
                <Input
                  aria-label="Credential reference"
                  value={credentialRef}
                  onChange={(event) => setCredentialRef(event.target.value)}
                  placeholder="cred:orion/jira"
                />
              </label>
              <label className="grid gap-1 text-sm">
                Comes from (optional)
                <Input
                  aria-label="Comes from"
                  value={source}
                  onChange={(event) => setSource(event.target.value)}
                  placeholder="Integration · GitHub App"
                />
              </label>
              <label className="grid gap-1 text-sm">
                Capabilities
                <Input
                  aria-label="Capabilities"
                  value={capabilities}
                  onChange={(event) => setCapabilities(event.target.value)}
                  placeholder="Read and comment on tickets"
                />
              </label>
              <div className="sm:col-span-2">
                <Button type="submit" disabled={!canRequest || requestMutation.isPending}>
                  <Plus aria-hidden className="size-4" />
                  {requestMutation.isPending ? "Requesting…" : "Request tool"}
                </Button>
              </div>
            </form>
            <p className="mt-3 text-xs text-muted-foreground">
              Separate capabilities with commas. The binding keeps exactly what you name, never
              more.
            </p>
          </CardContent>
        </Card>
      </section>

      <CapabilityDrawer
        item={selectedItem}
        kind="tool"
        onClose={() => setSelectedId(null)}
        onRemove={async () => {
          if (!project || !selected) return;
          await removeTool(project.id, selected.id);
          setFeedback("Tool removed.");
          refresh();
        }}
      />
    </div>
  );
}
